import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { StageArcs, OrbitSeedGraphic, WeekDots } from "@/components/vansha/Graphics";

export default function Pregnancy() {
  const [profile, setProfile] = useState(null);
  const [week, setWeek] = useState(null);
  const [selected, setSelected] = useState(null);
  const [allWeeks, setAllWeeks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [p, w] = await Promise.all([
        api.get("/pregnancy/profile").catch(() => ({ data: null })),
        api.get("/pregnancy/weeks"),
      ]);
      setProfile(p.data);
      setAllWeeks(w.data);
      const cur = p.data?.status?.current_week || 1;
      setSelected(cur);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (selected) api.get(`/pregnancy/week/${selected}`).then((r) => setWeek(r.data));
  }, [selected]);

  if (loading) return <div className="text-ink-500">Loading…</div>;
  if (!profile) return (
    <div data-testid="pregnancy-empty">
      <h1 className="font-serif text-4xl mb-3">Pregnancy</h1>
      <p className="text-ink-700 mb-6">You haven't set up your pregnancy yet.</p>
      <Link to="/app/onboarding" className="btn-primary" data-testid="goto-onboarding">Set up pregnancy</Link>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="pregnancy-page">
      <header className="relative overflow-hidden vansha-card-tinted-blush p-8 sm:p-10">
        <div className="absolute right-0 -top-4 opacity-90 hidden md:block pointer-events-none">
          <OrbitSeedGraphic size={260} />
        </div>
        <div className="relative z-10 max-w-xl">
          <p className="overline text-terracotta mb-2">Pregnancy operating system</p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl leading-none">
            Week <em className="text-terracotta font-normal italic">{profile.status.current_week}</em>
          </h1>
          <p className="text-ink-700 mt-3 leading-relaxed">Trimester {profile.status.trimester} · {profile.status.days_to_due > 0 ? `${profile.status.days_to_due} days to meet your baby` : "Due now"}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="chip chip-vermilion">Week {profile.status.current_week}</span>
            <span className="chip chip-marigold">Trimester {profile.status.trimester}</span>
            <span className="chip chip-sage">{profile.status.progress_pct}% along</span>
          </div>
        </div>
      </header>

      <div className="vansha-card p-2 overflow-x-auto" data-testid="week-selector">
        <div className="flex gap-1 min-w-max">
          {allWeeks.map((w) => (
            <button
              key={w.week}
              onClick={() => setSelected(w.week)}
              data-testid={`week-${w.week}`}
              className={`w-12 h-12 rounded-full text-sm font-medium flex-shrink-0 transition ${
                selected === w.week ? "bg-terracotta text-white" : profile.status.current_week === w.week ? "bg-saffron/20 text-terracotta" : "text-ink-700 hover:bg-vansha-accent"
              }`}
            >
              {w.week}
            </button>
          ))}
        </div>
      </div>

      {week && (
        <motion.div key={week.week} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 vansha-card p-8" data-testid="week-content">
            <p className="overline text-saffron mb-3">Week {week.week} · Trimester {week.trimester}</p>
            <h2 className="font-serif text-4xl leading-tight">Your baby is the size of {week.size_comparison}.</h2>
            <div className="mt-4 flex gap-6 text-sm text-ink-500">
              <span><strong className="text-ink-900">{week.baby_length_cm}cm</strong></span>
              <span><strong className="text-ink-900">{week.baby_weight_g}g</strong></span>
            </div>
            <div className="mt-8 space-y-6">
              <Section title="Baby this week">{week.baby_development}</Section>
              <Section title="For the mother">{week.mother_changes}</Section>
              <Section title="Nutrition">{week.nutrition}</Section>
            </div>
          </div>

          <div className="space-y-6">
            <div className="vansha-card p-6" data-testid="week-checklist">
              <h3 className="font-serif text-xl mb-4">This week</h3>
              <ul className="space-y-2 text-sm text-ink-700">
                {week.checklist.map((c, i) => (
                  <li key={i} className="flex gap-2"><span className="text-saffron">·</span> {c}</li>
                ))}
              </ul>
            </div>
            <div className="vansha-card p-6 bg-vansha-accent" data-testid="week-warning">
              <h3 className="font-serif text-xl mb-3 text-terracotta">Warning signs</h3>
              <p className="text-sm text-ink-700 mb-2">Contact your doctor immediately if you experience:</p>
              <ul className="space-y-1 text-sm text-ink-700">
                {week.warning_signs.map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="overline mb-2 text-ink-500">{title}</p>
      <p className="text-ink-700 leading-relaxed">{children}</p>
    </div>
  );
}
